import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RotateCcw, Crown, Wallet, TrendingUp, Trophy, MessageSquare, Calendar, Loader2 } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Module-level cache so re-hovering doesn't refire requests every time
const snapshotCache = new Map();
const CACHE_TTL_MS = 60_000; // 60s

const fmtUSD = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtCT = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return '—'; }
};

const fmtAccountAge = (iso) => {
    if (!iso) return '—';
    try {
        const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
        if (days < 30) return `${days}d old`;
        if (days < 365) return `${Math.floor(days / 30)}mo old`;
        return `${Math.floor(days / 365)}y old`;
    } catch { return '—'; }
};

/**
 * Responsible-play badge — shown on each pending cashout row.
 * Hovering opens a player snapshot card with one-glance context.
 *
 * Props:
 *   stats: { week_total, week_count, today_count } | null
 *   userId: string (required for hover-card fetch)
 */
export const PlayBackStatsBadge = ({ stats, userId }) => {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchSnapshot = useCallback(async () => {
        if (!userId) return;
        // Cache hit?
        const cached = snapshotCache.get(userId);
        if (cached && (Date.now() - cached.at) < CACHE_TTL_MS) {
            setSnapshot(cached.data);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(
                `${API}/admin/players/${userId}/snapshot`,
                { withCredentials: true }
            );
            snapshotCache.set(userId, { at: Date.now(), data });
            setSnapshot(data);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not load player');
        }
        setLoading(false);
    }, [userId]);

    // Reset cache on stats change (a play-back just happened) so badge stays fresh
    useEffect(() => {
        if (userId) snapshotCache.delete(userId);
        setSnapshot(null);
    }, [stats?.week_count, stats?.today_count, userId]);

    if (!stats || stats.week_count === 0) return null;
    const { week_total, week_count, today_count } = stats;

    // Neutral chip — playbacks are a customer-service tool, not a concerning
    // metric. We previously coloured red at 6+/day but the operator asked us
    // to remove the visual pressure so cashiers don't hesitate to refund.
    const color = '#A0AAB2';
    const bg = 'bg-white/5';
    const border = 'border-white/10';
    const Icon = RotateCcw;
    const label = '';

    return (
        <HoverCard openDelay={250} closeDelay={100} onOpenChange={(open) => { if (open) fetchSnapshot(); }}>
            <HoverCardTrigger asChild>
                <button
                    type="button"
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${bg} ${border} border cursor-help`}
                    style={{ color }}
                    data-testid="play-back-stats-badge"
                    aria-label="Player snapshot"
                >
                    <Icon className="w-3 h-3" />
                    {label && <span className="tracking-wider">{label}</span>}
                    ${week_total.toFixed(0)} / 7d · {week_count}×
                    {today_count > 0 && <span className="opacity-80">· {today_count} today</span>}
                </button>
            </HoverCardTrigger>
            <HoverCardContent
                align="start"
                sideOffset={6}
                className="w-80 bg-[#0a0a0a] border-white/10 p-0 text-white shadow-2xl"
                data-testid="player-snapshot-card"
            >
                {loading && !snapshot ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-[#FFD700]" />
                    </div>
                ) : error ? (
                    <div className="p-4 text-[#FF3B30] text-xs">{error}</div>
                ) : snapshot ? (
                    <div className="p-4 space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2 pb-2 border-b border-white/10">
                            <div className="min-w-0">
                                <div className="text-base font-bold text-white truncate">{snapshot.name}</div>
                                <div className="text-[10px] text-[#A0AAB2] truncate">{snapshot.email}</div>
                            </div>
                            <div className="flex flex-col items-end gap-0.5 shrink-0">
                                <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider"
                                    style={{ background: `${snapshot.vip_color}20`, color: snapshot.vip_color, border: `1px solid ${snapshot.vip_color}60` }}
                                >
                                    <Crown className="w-2.5 h-2.5" /> {snapshot.vip_name}
                                </span>
                                <span className="text-[9px] text-[#A0AAB2]">{fmtAccountAge(snapshot.created_at)}</span>
                            </div>
                        </div>

                        {/* Today indicator */}
                        {today_count > 0 && (
                            <div
                                className={`rounded-md px-3 py-2 text-xs font-bold flex items-center justify-between gap-2 ${
                                    today_count >= 6 ? 'bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40'
                                    : today_count >= 3 ? 'bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40'
                                    : 'bg-white/5 text-[#A0AAB2] border border-white/10'
                                }`}
                                data-testid="snapshot-today-row"
                            >
                                <span>{today_count} play-back{today_count === 1 ? '' : 's'} today</span>
                                {today_count >= 6 && <span className="text-[9px] tracking-wider">⚠ Watch closely</span>}
                            </div>
                        )}

                        {/* Stat grid */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <Stat icon={Wallet} label="Balance" value={fmtUSD(snapshot.credits)} color="#FFD700" />
                            <Stat icon={RotateCcw} label="Played back / 7d" value={`${fmtUSD(snapshot.play_back_week_total)} · ${snapshot.play_back_week_count}×`} color="#00E0FF" />
                            <Stat icon={TrendingUp} label="Avg load / day (30d)" value={fmtUSD(snapshot.avg_daily_deposit_30d)} color="#39FF14" />
                            <Stat icon={Wallet} label="Lifetime cash paid" value={fmtUSD(snapshot.lifetime_cash_paid)} color="#A0AAB2" />
                        </div>

                        {/* Last big win */}
                        {snapshot.last_big_win && (
                            <div className="rounded-md bg-[#FF007F]/10 border border-[#FF007F]/30 p-2 flex items-center gap-2" data-testid="snapshot-last-win">
                                <Trophy className="w-4 h-4 text-[#FF007F] shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Last big win</div>
                                    <div className="text-sm font-bold text-[#FF007F]">{fmtUSD(snapshot.last_big_win.amount)}</div>
                                    <div className="text-[10px] text-[#A0AAB2] truncate">{fmtCT(snapshot.last_big_win.at)}</div>
                                </div>
                            </div>
                        )}

                        {/* Last load */}
                        {snapshot.last_load && (
                            <div className="flex items-center gap-2 text-[11px] text-[#A0AAB2]">
                                <Calendar className="w-3 h-3" />
                                Last load: <span className="text-white font-bold">{fmtUSD(snapshot.last_load.amount)}</span> · {fmtCT(snapshot.last_load.at)}
                            </div>
                        )}

                        {snapshot.phone && (
                            <a
                                href={`sms:${snapshot.phone.replace(/[^0-9+]/g, '')}`}
                                className="flex items-center gap-2 text-[11px] text-[#FFD700] hover:underline"
                            >
                                <MessageSquare className="w-3 h-3" /> {snapshot.phone}
                            </a>
                        )}
                    </div>
                ) : (
                    <div className="p-4 text-[#A0AAB2] text-xs">Hover to load player info</div>
                )}
            </HoverCardContent>
        </HoverCard>
    );
};

const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="rounded-md bg-black/30 border border-white/5 px-2.5 py-1.5">
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold" style={{ color }}>
            <Icon className="w-2.5 h-2.5" /> {label}
        </div>
        <div className="font-mono text-sm font-black text-white truncate">{value}</div>
    </div>
);

export default PlayBackStatsBadge;
